import api from "../utils/axiosConfig";
import type { BetOption } from "../utils/gameRules";
import { dedupeInFlight } from "../utils/inFlight";

// ==================== Types ====================

export type { BetOption };

export type RoundStatus = "OPEN" | "LOCKED" | "DRAWING" | "SETTLED";

export type BetStatus = "PENDING" | "WON" | "LOST" | "VOID";

export interface GameConfig {
  game_code: string;
  game_name: string;
  draw_interval_seconds: number;
  position_count: number;
  min_stake: number;
  max_stake: number;
  currency: string;
  odds: Record<BetOption, number>;
  quick_amounts: number[];
}

export interface CurrentRound {
  draw_no?: number;
  period_id: string;
  status: RoundStatus;
  opens_at: string;
  closes_at: string;
  draw_at: string;
  server_time: string;
}

export interface BetSelection {
  position: number;
  option: BetOption;
}

export interface BetEntry extends BetSelection {
  amount: number;
}

export interface PlaceBetRequest {
  draw_no?: number;
  period_id: string;
  bets: BetEntry[];
}

export interface Bet {
  bet_id: string;
  period_id: string;
  bets?: BetEntry[];
  selections: BetSelection[];
  stake_per_selection: number;
  total_stake: number;
  status: BetStatus;
  payout: number;
  created_at: string;
  balance_after?: number;
}

export interface GameResult {
  game_number: string;
  numbers: number[];
  drawn_at: string;
}

export type GameConfigApiResponse = {
  status: number;
  system: string;
  message: string;
  data: GameConfig;
};

export type CurrentRoundApiResponse = {
  status: number;
  system: string;
  message: string;
  data: CurrentRound;
};

export interface PaginationParams {
  page: number;
  limit: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_more: boolean;
}

export type RecentResultsApiResponse = {
  status: number;
  system: string;
  message: string;
  data: GameResult[];
};

export type PaginatedRecentResultsApiResponse = RecentResultsApiResponse & {
  pagination: PaginationMeta;
};

export type MyBetsApiResponse = {
  status: number;
  system: string;
  message: string;
  data: Bet[];
};

export type PlaceBetApiResponse = {
  status: number;
  system: string;
  message: string;
  data: Bet;
};

// ==================== Endpoints ====================

const ENDPOINTS = {
  rules: "/rules",
  currentRound: "/draws/current",
  recentResults: "/draws/recent",
  myBets: "/me/bets",
  placeBet: "/bets",
};

export type ApiRules = {
  intervalSeconds: number;
  positionCount: number;
  oddsMultiplier: number;
  betTypes: Record<string, { oddsBasisPoints: number }>;
  stakeLimits: { minCents: number; maxCents: number };
};

export type ApiDraw = {
  drawNo: number;
  periodCode: string;
  positionCount: number;
  opensAt: string;
  closesAt: string;
  betsCloseAt: string;
  isBettingOpen: boolean;
  status: string;
  results: number[];
  drawnAt?: string;
};

type ApiBetResponse = {
  drawNo: number;
  periodCode: string;
  accepted: number;
  totalStakeCents: number;
  balanceCents: number;
  reference?: string;
};

type ApiPlayerBet = {
  id: string;
  drawNo: number;
  position: number;
  betType: string;
  stakeCents: number;
  status: string;
  payoutCents: number;
  createdAt: string;
};

const toLegacyBetStatus = (status: string): BetStatus => {
  if (status === "won") return "WON";
  if (status === "lost") return "LOST";
  if (status === "voided" || status === "refunded") return "VOID";
  return "PENDING";
};

const toLegacyBet = (bet: ApiPlayerBet): Bet => ({
  bet_id: bet.id,
  period_id: String(bet.drawNo),
  selections: [{
    position: bet.position,
    option: bet.betType.toUpperCase() as BetOption,
  }],
  stake_per_selection: bet.stakeCents / 100,
  total_stake: bet.stakeCents / 100,
  status: toLegacyBetStatus(bet.status),
  payout: bet.payoutCents / 100,
  created_at: bet.createdAt,
});

const toRoundStatus = (status: string, isBettingOpen = false): RoundStatus => {
  if (status === "open" && isBettingOpen) return "OPEN";
  if (status === "open") return "LOCKED";
  if (status === "drawn") return "DRAWING";
  return status === "settled" ? "SETTLED" : "LOCKED";
};

export const toGameResult = (draw: ApiDraw): GameResult => ({
  game_number: String(draw.drawNo),
  numbers: draw.results,
  drawn_at: draw.drawnAt || draw.closesAt,
});

export const toConfig = (rules: ApiRules): GameConfig => ({
  game_code: "LOTTO",
  game_name: "Lotto",
  draw_interval_seconds: rules.intervalSeconds,
  position_count: rules.positionCount,
  min_stake: rules.stakeLimits.minCents / 100,
  max_stake: rules.stakeLimits.maxCents / 100,
  currency: "MYR",
  odds: {
    BIG: (rules.betTypes.big?.oddsBasisPoints ?? 0) / 10000,
    SMALL: (rules.betTypes.small?.oddsBasisPoints ?? 0) / 10000,
    ODD: (rules.betTypes.odd?.oddsBasisPoints ?? 0) / 10000,
    EVEN: (rules.betTypes.even?.oddsBasisPoints ?? 0) / 10000,
  },
  quick_amounts: [],
});

export const toCurrentRound = (draw: ApiDraw): CurrentRound => ({
  draw_no: draw.drawNo,
  period_id: draw.periodCode,
  status: toRoundStatus(draw.status, draw.isBettingOpen),
  opens_at: draw.opensAt,
  closes_at: draw.betsCloseAt,
  draw_at: draw.closesAt,
  server_time: new Date().toISOString(),
});

// ==================== API Functions ====================

export const getGameConfig = async (): Promise<GameConfigApiResponse> => {
  try {
    const response = await api.get<ApiRules>(ENDPOINTS.rules);
    return {
      status: 1,
      system: "game",
      message: "Game rules retrieved",
      data: toConfig(response.data),
    };
  } catch (error) {
    console.error("Get game config error:", error);
    throw error;
  }
};

export const getCurrentRound = async (): Promise<CurrentRoundApiResponse> => {
  try {
    const response = await dedupeInFlight(
      "GET /draws/current",
      () => api.get<ApiDraw>(ENDPOINTS.currentRound),
    );
    const draw = response.data;
    return {
      status: 1,
      system: "game",
      message: "Current draw retrieved",
      data: toCurrentRound(draw),
    };
  } catch (error) {
    console.error("Get current round error:", error);
    throw error;
  }
};

export const RESULTS_PAGE_LIMIT = 25;

const buildPagination = (
  { page, limit }: PaginationParams,
  total: number
): PaginationMeta => ({
  page,
  limit,
  total,
  total_pages: Math.max(1, Math.ceil(total / limit)),
  has_more: page * limit < total,
});

export const getRecentResults = async ({
  page = 1,
  limit = RESULTS_PAGE_LIMIT,
}: Partial<PaginationParams> = {}): Promise<PaginatedRecentResultsApiResponse> => {
  const params: PaginationParams = { page, limit };

  try {
    const response = await dedupeInFlight(
      `GET /draws/recent?limit=${limit}`,
      () => api.get<{ items: ApiDraw[] }>(ENDPOINTS.recentResults, { params: { limit } }),
    );
    const data = response.data.items.map(toGameResult);
    return {
      status: 1,
      system: "game",
      message: "Recent results retrieved",
      data,
      pagination: buildPagination(params, data.length),
    };
  } catch (error) {
    console.error("Get recent results error:", error);
    throw error;
  }
};

export const fetchLatestGameResult = async (positionCount: number): Promise<GameResult> => {
  try {
    const response = await api.get<{ items: ApiDraw[] }>(ENDPOINTS.recentResults, {
      params: { limit: 1 },
    });
    const result = response.data.items[0];
    if (!result) throw new Error("Latest game result is missing");
    return toGameResult(result);
  } catch (error) {
    console.error("Get latest game result error:", error);
    throw error;
  }
};

export const getMyBets = async (
  page: number = 1,
  limit: number = 20
): Promise<MyBetsApiResponse> => {
  try {
    const response = await api.get<{
      items: ApiPlayerBet[];
      total: number;
      page: number;
      perPage: number;
      pages: number;
    }>(ENDPOINTS.myBets, {
      params: { page, perPage: limit },
    });
    return {
      status: 1,
      system: "game",
      message: "Bet history retrieved",
      data: response.data.items.map(toLegacyBet),
    };
  } catch (error) {
    console.error("Get my bets error:", error);
    throw error;
  }
};

export const placeBet = async (
  payload: PlaceBetRequest
): Promise<PlaceBetApiResponse> => {
  try {
    const response = await api.post<ApiBetResponse>(ENDPOINTS.placeBet, {
      drawNo: payload.draw_no ?? Number(payload.period_id.replace(/\D/g, "")),
      selections: payload.bets.map((bet) => ({
        position: bet.position,
        betType: bet.option.toLowerCase(),
        stakeCents: Math.round(bet.amount * 100),
      })),
    });
    return {
      status: 1,
      system: "game",
      message: "Bet placed successfully",
      data: {
        bet_id: response.data.reference || String(response.data.drawNo),
        period_id: response.data.periodCode,
        bets: payload.bets,
        selections: payload.bets.map(({ position, option }) => ({ position, option })),
        stake_per_selection: payload.bets[0]?.amount ?? 0,
        total_stake: response.data.totalStakeCents / 100,
        status: "PENDING",
        payout: 0,
        created_at: new Date().toISOString(),
        balance_after: response.data.balanceCents / 100,
      },
    };
  } catch (error) {
    console.error("Place bet error:", error);
    throw error;
  }
};
